import React, { useEffect, useState, createContext } from 'react';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import WelcomeSlideshow from './components/WelcomeSlideshow';
import Intro from './components/Intro';
import AboutMe from './components/AboutMe';
import Services from './components/Services';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import './App.css';
import './styles/css/fonts.css';
import translations from './translations';

export const LanguageContext = createContext();

const theme = createTheme({
  palette: {
    primary: {
      main: '#3a4752',
    },
    secondary: {
      main: '#e6c7c2',
    },
    background: {
      default: '#f8f9fa',
    },
  },
  typography: {
    fontFamily: '"Noto Sans Hebrew", "Roboto", "Helvetica", "Arial", sans-serif',    
    h1: {
      fontWeight: 300,
    },
    h2: {
      fontWeight: 300,
    },
    h3: {
      fontWeight: 400,
    },
  },
});

const getLanguageFromPath = () => {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.split('/').includes('en')
  ) {
    return 'en';
  }
  return 'he';
};

function App() {
  const [isMounted, setIsMounted] = useState(false);
  const [domReady, setDomReady] = useState(false);
  const [language, setLanguage] = useState(getLanguageFromPath());

  useEffect(() => {
    // Mark component as mounted after initial render
    setIsMounted(true);

    // Add a class to indicate JavaScript is enabled
    if (typeof document !== 'undefined') {
      document.documentElement.classList.add('js-enabled');
    }

    // Set a delay to ensure DOM is fully processed
    const timer = setTimeout(() => {
      setDomReady(true);
    }, 200);

    // Listen for path changes to update language
    const handlePathChange = () => {
      setLanguage(getLanguageFromPath());
    };
    window.addEventListener('popstate', handlePathChange);
    window.addEventListener('pushstate', handlePathChange);
    window.addEventListener('replaceState', handlePathChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('popstate', handlePathChange);
      window.removeEventListener('pushstate', handlePathChange);
      window.removeEventListener('replaceState', handlePathChange);
    };
  }, []);

  // Show a simple loading state during initial render
  if (!isMounted) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LanguageContext.Provider value={{ language, setLanguage, translations }}>
          <div className="App" dir={language === 'he' ? 'rtl' : 'ltr'}>
            {domReady ? (
              <>
                <Navbar />
                <main>
                  <section id="home">
                    <WelcomeSlideshow />
                  </section>
                  <section id="intro">
                    <Intro />
                  </section>
                  <section id="about">
                    <AboutMe />
                  </section>                
                  <section id="portfolio">
                    <Portfolio />
                  </section>
                  <section id="services">
                    <Services />
                  </section>
                  <section id="testimonials">
                    <Testimonials />
                  </section>
                  <section id="faq">
                    <FAQ />
                  </section>
                  <section id="contact">
                    <Contact />
                  </section>
                </main>
                <Footer />
                <ScrollToTop />
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Preparing your experience...</div>
              </div>
            )}
          </div>
        </LanguageContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
